import { CircularProgress, useTheme } from "@material-ui/core";
import { STATUS_MESSAGE_IDS } from "../config";
import React, { useContext, useEffect } from "react";
import { ProcessContext } from "../../../../../contexts/ProcessContext";
import { ConfigContext } from "../../../../../contexts/ConfigContext";
import { ThemedAsset, assetIds } from "nemuru-components";
import { AssetType, formatMoneyAmount } from "@nemuru-eng/js-api-client";
import Text from "../../../../shared/components/Text";
import { RedirectToMerchantButton } from "../../../../shared/components/RedirectToMerchantButton";

export const useInfoMessages = () => {
  const { palette } = useTheme();
  const {
    state: { loan, asset, participant, signatureChannel },
    dispatch,
  } = useContext(ProcessContext);
  const {
    state: { dictionaryValues },
  } = useContext(ConfigContext);

  useEffect(() => {
    if (loan && isAssetTypeCard) {
      dispatch({
        type: "REQUEST_GET_ASSET_DETAILS",
        payload: {
          assetId: loan.asset_reference[0].id,
          assetType: loan.asset_reference[0].type,
        },
      });
    }
  }, [loan]);

  const isAssetTypeCard = loan?.asset_reference?.[0]?.type === AssetType.CARD;

  const onboardingDetailsArray = loan.onboarding_details;

  const incompleteOnboardingParticipantsArray = (onboardingDetailsArray) => {
    return onboardingDetailsArray
      .map((participantOnboardingDetails) => {
        if (!participantOnboardingDetails?.is_completed) {
          return `${
            participantOnboardingDetails.invitation.full_name.first_name
          } ${participantOnboardingDetails.invitation.full_name.last_name} 
          (${dictionaryValues[participantOnboardingDetails.role]})`;
        }
      })
      .filter((el) => el !== undefined);
  };

  const getLoanOnboardingsListStatuses = (onboardingDetailsArray) => {
    return onboardingDetailsArray.map((details) => {
      const isOnboardingCompletedByParticipant = details.is_completed;

      return {
        mainTextId: details.role,
        additionalTextId: isOnboardingCompletedByParticipant
          ? "ONBOARDING_COMPLETED"
          : "PENDING_ONBOARDING",
        icon: isOnboardingCompletedByParticipant
          ? ListItemIconDone
          : ListItemIconUndone,
      };
    });
  };

  const ConfirmationCheck = (
    <ThemedAsset
      id={assetIds.ICON_SUCCESS}
      width={50}
      height={50}
      style={{ color: palette.success.main }}
    />
  );

  const DownPaymentPending = (
    <ThemedAsset
      id={assetIds.ICON_MONEY_INCOMES}
      width={50}
      height={50}
      style={{ color: palette.success.main }}
    />
  );

  const FailIcon = (
    <ThemedAsset
      id={assetIds.ICON_FAIL}
      width={50}
      height={50}
      style={{ color: palette.error.main }}
    />
  );

  const ListItemIconDone = (
    <i
      className="material-icons-outlined"
      style={{
        fontSize: 18,
        color: palette.success.secondary,
      }}
    >
      check_circle
    </i>
  );

  const ListItemIconUndone = (
    <i
      className="material-icons-outlined"
      style={{
        fontSize: 18,
        color: palette.text.secondary,
      }}
    >
      hourglass_empty
    </i>
  );

  const ListItemLoading = (
    <CircularProgress
      className={"mt-1"}
      size={20}
      style={{ color: palette.text.secondary }}
      thickness={5}
    />
  );

  const {
    ANALYSING_END,
    ANALYSING_UPLOAD_DOCUMENTATION,
    ANALYSING_DOCUMENTS_UPLOADED,
    ANALYSING_SIGNATURE_NOT_AVAILABLE,
    ANALYSING_PENDING_APPROVAL,
    PREAPPROVED_DETACHED_SIGN_END,
    PREAPPROVED_SIGNATURE_REDIRECT,
    PREAPPROVED_SIGNATURE_REDIRECT_NO_DOCS_NEEDED,
    PREAPPROVED_DOCS_AND_SIGNATURE_REDIRECT,
    CONTRACT_SIGNED_END,
    CONTRACT_SIGNED_NO_DOCS_NEEDED_END,
    CONTRACT_SIGNED_DOCS_NEEDED,
    APPROVED_END,
    DENIED_END,
    DENIED_AUTO_CPC,
    PREAPPROVED_LOADING_REMAINING_ACTIONS,
    LOAN_ONBOARDINGS_ARE_NOT_COMPLETED,
    LENDER_HAS_BEEN_SELECTED_UPLOAD_DOCUMENTATION,
    LENDER_HAS_BEEN_SELECTED_DOCUMENTS_UPLOADED,
    PREAPPROVED_DOCUMENTS_UPLOADED_SIGNATURE_NOT_AVAILABLE,
    INVALID_DOWN_PAYMENT_CHECKOUT_SESSION_FOR_SIGNATURE,
    LENDER_HAS_BEEN_SELECTED_PENDING_DOWN_PAYMENT_UPLOAD_DOCUMENTATION,
  } = STATUS_MESSAGE_IDS;

  const docsValidationInstruction = {
    mainTextId: "DOCS_VALIDATION_PENDING",
    additionalTextId: "DOCS_VALIDATION_PENDING_INFO",
    icon: ListItemIconUndone,
  };

  const docsPendingInstructions = {
    title: "ONE_ACTION_REMAINING",
    titleDynamicDataTexts: participant.onboardingDetails.invitation.email,
    listItems: [
      {
        mainTextId: "DOCS_PENDING",
        additionalTextId: "DOCS_PENDING_INFO",
        icon: ListItemIconUndone,
      },
    ],
  };

  const docsAndSignaturePendingInstructions = {
    title: isAssetTypeCard
      ? "THREE_ACTIONS_REMAINING"
      : "TWO_ACTIONS_REMAINING",
    listItems: [
      {
        mainTextId: "DOCS_PENDING",
        additionalTextId: "DOCS_PENDING_INFO",
        icon: ListItemIconUndone,
      },
      {
        mainTextId: "SIGNATURE_PENDING",
        additionalTextId: "SIGNATURE_PENDING_INFO",
        icon: ListItemIconUndone,
      },
      ...(isAssetTypeCard ? [docsValidationInstruction] : []),
    ],
  };

  const docsUploadedSignaturePendingInstructions = {
    title: isAssetTypeCard ? "TWO_ACTIONS_REMAINING" : "ONE_ACTION_REMAINING",
    titleDynamicDataTexts: participant.onboardingDetails.invitation.email,
    listItems: [
      {
        mainTextId: "DOCS_COMPLETED",
        additionalTextId: null,
        icon: ListItemIconDone,
      },
      {
        mainTextId: "SIGNATURE_PENDING",
        additionalTextId: "SIGNATURE_PENDING_INFO",
        icon: ListItemIconUndone,
      },
      ...(isAssetTypeCard ? [docsValidationInstruction] : []),
    ],
  };

  const docsUploadedAndSignedInstructions = {
    title: isAssetTypeCard ? "ONE_ACTION_REMAINING" : "NO_ACTION_REMAINING",
    titleDynamicDataTexts: participant.onboardingDetails.invitation.email,
    listItems: [
      {
        mainTextId: "DOCS_COMPLETED",
        additionalTextId: null,
        icon: ListItemIconDone,
      },
      {
        mainTextId: "SIGNATURE_COMPLETED",
        additionalTextId: null,
        icon: ListItemIconDone,
      },
      ...(isAssetTypeCard ? [docsValidationInstruction] : []),
    ],
  };

  const getCreditLimitNoticeDetailObject = () => {
    if (!isAssetTypeCard || !asset || !asset.card_requested_limit) {
      return null;
    }

    return (
      <strong>
        <Text
          tId="CREDIT_LIMIT_NOTICE"
          dynamicDataTexts={[formatMoneyAmount(asset.card_requested_limit)]}
        />
      </strong>
    );
  };

  const docsPendingAndSignedInstructions = {
    title: isAssetTypeCard ? "TWO_ACTIONS_REMAINING" : "ONE_ACTION_REMAINING",
    titleDynamicDataTexts: participant.onboardingDetails.invitation.email,
    listItems: [
      {
        mainTextId: "DOCS_PENDING",
        additionalTextId: "DOCS_PENDING_INFO",
        icon: ListItemIconUndone,
      },
      {
        mainTextId: "SIGNATURE_COMPLETED",
        additionalTextId: null,
        icon: ListItemIconDone,
      },
      ...(isAssetTypeCard ? [docsValidationInstruction] : []),
    ],
  };

  const messages = {
    [PREAPPROVED_LOADING_REMAINING_ACTIONS]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED",
      detail: null,
      instructions: {
        title: null,
        listItems: [
          {
            mainTextId: "WAITING_FOR_REMAINING_ACTIONS",
            additionalTextId: null,
            icon: ListItemLoading,
          },
        ],
      },
      button: null,
      infoMessage: null,
    },
    [ANALYSING_UPLOAD_DOCUMENTATION]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED",
      detail: getCreditLimitNoticeDetailObject(),
      instructions: docsPendingInstructions,
      button: "UPLOAD_DOCS_NOW",
      infoMessage: null,
    },
    [ANALYSING_DOCUMENTS_UPLOADED]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATION_DOCUMENTS_UPLOADED",
      subtitle: "THANKS_DOCUMENTATION_UPLOADED",
      detail: "LOAN_ANALYSING",
      instructions: { title: null },
      button: <RedirectToMerchantButton />,
      infoMessage: null,
    },
    [LENDER_HAS_BEEN_SELECTED_UPLOAD_DOCUMENTATION]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED",
      detail: getCreditLimitNoticeDetailObject(),
      instructions: docsAndSignaturePendingInstructions,
      button: "UPLOAD_DOCS_NOW",
      infoMessage: null,
    },
    [LENDER_HAS_BEEN_SELECTED_PENDING_DOWN_PAYMENT_UPLOAD_DOCUMENTATION]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED",
      detail: null,
      instructions: {
        title: "THREE_ACTIONS_REMAINING",
        listItems: [
          {
            mainTextId: "DOCS_PENDING",
            additionalTextId: "DOCS_PENDING_INFO",
            icon: ListItemIconUndone,
          },
          {
            mainTextId: "DOWN_PAYMENT_PENDING",
            additionalTextId:
              "YOU_MUST_PAY_THE_DOWN_PAYMENT_BEFORE_SIGNING_THE_CONTRACT",
            icon: ListItemIconUndone,
          },
          {
            mainTextId: "SIGNATURE_PENDING",
            additionalTextId: "SIGNATURE_PENDING_INFO",
            icon: ListItemIconUndone,
          },
        ],
      },
      button: "UPLOAD_DOCS_NOW",
      infoMessage: null,
    },
    [LENDER_HAS_BEEN_SELECTED_DOCUMENTS_UPLOADED]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_DOCUMENTATION_UPLOADED",
      instructions: docsUploadedSignaturePendingInstructions,
      // button: onBoardingByBorrower ? "SIGN_NOW" : null,
      button: "SIGN_NOW",
      infoMessage: null,
    },
    [ANALYSING_END]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED",
      detail: "LOAN_ANALYSING",
      instructions: {},
      button: <RedirectToMerchantButton />, // add "pending" param when available
      infoMessage: null,
    },
    [ANALYSING_PENDING_APPROVAL]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED",
      detail: "LENDER_SELECTED_PENDING_APPROVAL",
      instructions: {},
      button: <RedirectToMerchantButton />, // add "pending" param when available
      infoMessage: null,
    },
    [PREAPPROVED_SIGNATURE_REDIRECT]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED",
      detail: getCreditLimitNoticeDetailObject(),
      instructions: docsUploadedSignaturePendingInstructions,
      // button: onBoardingByBorrower ? "SIGN_NOW" : null,
      button: "SIGN_NOW",
      infoMessage: null,
    },
    [PREAPPROVED_SIGNATURE_REDIRECT_NO_DOCS_NEEDED]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED",
      detail: null,
      instructions: {
        title: "ONE_ACTION_REMAINING",
        listItems: [
          {
            mainTextId: "SIGNATURE_PENDING",
            additionalTextId: "SIGNATURE_PENDING_INFO",
            icon: ListItemIconUndone,
          },
        ],
      },
      // button: onBoardingByBorrower ? "SIGN_NOW" : null,
      button: "SIGN_NOW",
      infoMessage: null,
    },
    [PREAPPROVED_DETACHED_SIGN_END]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED",
      detail: getCreditLimitNoticeDetailObject(),
      instructions: docsUploadedSignaturePendingInstructions,
      button: <RedirectToMerchantButton />,
      infoMessage:
        signatureChannel?.action || "SIGNATURE_PENDING_BY_MAIL_INFO_MESSAGE",
    },
    [PREAPPROVED_DOCS_AND_SIGNATURE_REDIRECT]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle:
        "THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED_DOCS_AND_SIGNATURE_REDIRECT",
      detail: getCreditLimitNoticeDetailObject(),
      instructions: docsAndSignaturePendingInstructions,
      button: "UPLOAD_DOCS_NOW",
      infoMessage: null,
    },
    [CONTRACT_SIGNED_END]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED",
      detail: null,
      instructions: docsUploadedAndSignedInstructions,
      button: <RedirectToMerchantButton />,
      infoMessage: "YOU_CAN_SAFELY_CLOSE_THIS_WINDOW",
    },
    [CONTRACT_SIGNED_NO_DOCS_NEEDED_END]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED",
      detail: null,
      instructions: {
        title: "NO_ACTION_REMAINING",
        listItems: [
          {
            mainTextId: "SIGNATURE_COMPLETED",
            additionalTextId: null,
            icon: ListItemIconDone,
          },
        ],
      },
      button: <RedirectToMerchantButton />,
      infoMessage: "YOU_CAN_SAFELY_CLOSE_THIS_WINDOW",
    },
    [PREAPPROVED_DOCUMENTS_UPLOADED_SIGNATURE_NOT_AVAILABLE]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "PREAPPROVED_DOCUMENTS_UPLOADED_SIGNATURE_NOT_AVAILABLE",
      detail: null,
      instructions: {
        title: "SIGNATURE_MODULE_YET_NOT_AVAILABLE",
        style: { textAlign: "center" },
      },
      button: <RedirectToMerchantButton />,
    },
    [ANALYSING_SIGNATURE_NOT_AVAILABLE]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS_SIGNATURE_NOT_AVAILABLE",
      subtitle: "APPLICATION_PENDING_APPROVAL",
      detail: null,
      instructions: {
        title: "SIGNATURE_MODULE_YET_NOT_AVAILABLE",
        style: { textAlign: "center" },
      },
      button: <RedirectToMerchantButton />,
    },
    [INVALID_DOWN_PAYMENT_CHECKOUT_SESSION_FOR_SIGNATURE]: {
      asset: DownPaymentPending,
      color: palette.primary.main,
      title: "THANKS",
      subtitle: "INVALID_DOWN_PAYMENT_CHECKOUT_SESSION_FOR_SIGNATURE",
      detail: null,
      instructions: {
        title: "CLOSE_THE_DIALOG_AND_PAY_THE_DOWN_PAYMENT",
        style: { textAlign: "center" },
      },
      button: null,
    },
    [LOAN_ONBOARDINGS_ARE_NOT_COMPLETED]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED",
      detail: null,
      instructions: {
        title: "LOAN_ONBOARDINGS_ARE_NOT_COMPLETED",
        titleDynamicDataTexts: incompleteOnboardingParticipantsArray(
          onboardingDetailsArray
        ).join(", "),
        listItems: getLoanOnboardingsListStatuses(onboardingDetailsArray),
      },
      button: <RedirectToMerchantButton />, // add "pending" param when available
      infoMessage: "PENDING_ONBOARDING_INFO",
    },
    [CONTRACT_SIGNED_DOCS_NEEDED]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED_AND_PREAPPROVED",
      detail: null,
      instructions: docsPendingAndSignedInstructions,
      button: "UPLOAD_DOCS_NOW",
      infoMessage: null,
    },
    [APPROVED_END]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS_APPROVED_END",
      subtitle: "APPROVED",
      detail: null,
      instructions: {
        title: null,
        listItems: null,
      },
      button: <RedirectToMerchantButton />,
      infoMessage: null,
    },
    [DENIED_END]: {
      asset: FailIcon,
      color: palette.error.main,
      title: "WE_ARE_SORRY",
      subtitle: "LOAN_HAS_BEEN_DENIED",
      detail: "SYSTEM_FAILURE_DETECTION",
      instructions: {
        title: null,
        listItems: null,
      },
      button: <RedirectToMerchantButton />,
      infoMessage: null,
    },
    [DENIED_AUTO_CPC]: {
      asset: ConfirmationCheck,
      color: palette.primary.main,
      title: "CONGRATULATIONS",
      subtitle: "THANKS_LOAN_FORM_COMPLETED",
      detail: "YOUR_COMMERCIAL_AGENT_WILL_CONTACT_YOU_SOON_FOR_MORE_DETAILS",
      instructions: {
        title: null,
        listItems: null,
      },
      button: <RedirectToMerchantButton />,
      infoMessage: null,
    },
    ERROR: {
      asset: FailIcon,
      color: palette.error.main,
      title: "OOPS",
      instructions: {},
    },
  };
  return { messages };
};
